import { useLingui } from '@lingui/react/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, keyframes, Stack, Typography } from '@mui/material';
import { type MouseEvent, memo, useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { useDisconnect } from 'wagmi';
import { DepositPanel } from '../../../features/account/components/deposit_panel.tsx';
import { WithdrawPanel } from '../../../features/account/components/withdraw_panel.tsx';
import useAppStore from '../../../store/rootStore';
import { HoverMenu } from '../hover_menu.tsx';
import { isHoverMovingWithin } from '../use_hover_menu.ts';
import { AccountMenuOverview } from './account_menu_overview.tsx';
import { ACCOUNT_MENU_WIDTH } from './account_menu_styles.ts';
import { FeesPanel, getAccountFeeTierLabel } from './fees_panel.tsx';

export type AccountMenuView = 'overview' | 'deposit' | 'withdraw' | 'fees';
type DetailView = Exclude<AccountMenuView, 'overview'>;

const menuCardSx = {
  width: ACCOUNT_MENU_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
  height: 'fit-content',
  overflow: 'hidden',
} as const;

const SWIPE_MS = 280;
const SWIPE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const swipeFromRight = keyframes`
  from { transform: translateX(30%); opacity: 0.5; }
  to { transform: translateX(0); opacity: 1; }
`;

const swipeFromLeft = keyframes`
  from { transform: translateX(-30%); opacity: 0.5; }
  to { transform: translateX(0); opacity: 1; }
`;

const swipeFromBottom = keyframes`
  from { transform: translateY(16%); opacity: 0.6; }
  to { transform: translateY(0); opacity: 1; }
`;

const swipeFromTop = keyframes`
  from { transform: translateY(-12%); opacity: 0.6; }
  to { transform: translateY(0); opacity: 1; }
`;

type SwipeDir = 'left' | 'right' | 'bottom' | 'top';

const swipeAnimation = {
  left: swipeFromLeft,
  right: swipeFromRight,
  bottom: swipeFromBottom,
  top: swipeFromTop,
} as const;

/** Where each detail view slides in from, and back out to. */
const swipeDirs: Record<DetailView, { enter: SwipeDir; exit: SwipeDir }> = {
  deposit: { enter: 'left', exit: 'right' },
  withdraw: { enter: 'right', exit: 'left' },
  fees: { enter: 'bottom', exit: 'top' },
};

const paneSx = {
  px: 1.5,
  pt: 1.5,
  pb: 1.25,
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
} as const;

interface UserAccountMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  /** False until first open so the deposit/withdraw/fees trees aren't mounted on page load. */
  mounted?: boolean;
  onClose: () => void;
  onEnter: () => void;
  onLeave: () => void;
  canWithdraw: boolean;
  showReferral: boolean;
  ensName?: string | null;
  ensAvatar?: string | null;
  view: AccountMenuView;
  onViewChange: (view: AccountMenuView) => void;
  triggerEl?: HTMLElement | null;
  onRootRef?: (node: HTMLElement | null) => void;
}

function MenuHeader({ title, onBack }: { title: string; onBack?: () => void }) {
  const { t } = useLingui();
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        mx: -1.5,
        mt: -1.5,
        mb: 1.25,
        px: 1.75,
        py: 1.25,
        minHeight: 42,
        borderBottom: '1px solid',
        borderColor: 'elevation.outlined',
      }}
    >
      {onBack ? (
        <IconButton
          onClick={onBack}
          size="small"
          aria-label={t({ comment: 'Back to the account menu overview', message: 'Back' })}
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'text.secondary', letterSpacing: 0.02 }}>
        {title}
      </Typography>
    </Stack>
  );
}

export const UserAccountMenu = memo(function UserAccountMenu({
  anchorEl,
  open,
  mounted = true,
  onClose,
  onEnter,
  onLeave,
  canWithdraw,
  showReferral,
  ensName,
  ensAvatar,
  view,
  onViewChange,
  triggerEl,
  onRootRef,
}: UserAccountMenuProps) {
  const menuId = useId();
  const menuRootRef = useRef<HTMLDivElement | null>(null);
  const prevViewRef = useRef<AccountMenuView>(view);
  const [swipe, setSwipe] = useState<{ id: number; dir: SwipeDir } | null>(null);
  const { t } = useLingui();
  const { disconnect } = useDisconnect();
  const accountFeeTier = useAppStore((state) => state.account?.ft);
  const feeTierLabel = getAccountFeeTierLabel(accountFeeTier);

  useLayoutEffect(() => {
    if (!open) {
      prevViewRef.current = view;
      setSwipe(null);
      return;
    }

    const prev = prevViewRef.current;
    if (prev === view) return;

    const detail = view !== 'overview' ? view : prev !== 'overview' ? prev : null;
    if (detail) setSwipe({ id: Date.now(), dir: view === 'overview' ? swipeDirs[detail].exit : swipeDirs[detail].enter });
    prevViewRef.current = view;
  }, [open, view]);

  const setMenuRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuRootRef.current = node;
      onRootRef?.(node);
    },
    [onRootRef]
  );

  const handleBack = useCallback(() => onViewChange('overview'), [onViewChange]);

  const handleDisconnect = useCallback(() => {
    disconnect();
    onClose();
  }, [disconnect, onClose]);

  const handleMenuLeave = useCallback(
    (event: MouseEvent) => {
      if (isHoverMovingWithin(event.relatedTarget, [triggerEl, menuRootRef.current])) return;
      onLeave();
    },
    [onLeave, triggerEl]
  );

  const handleEscape = useCallback(() => {
    onClose();
    triggerEl?.focus();
  }, [onClose, triggerEl]);

  const detailTitle =
    view === 'withdraw'
      ? t({ comment: 'Withdraw view title in account menu', message: 'Withdraw' })
      : view === 'fees'
        ? feeTierLabel
          ? t({ comment: 'Fees view title in account menu with user tier', message: `Fees Tier ${feeTierLabel}` })
          : t({ comment: 'Fees view title in account menu', message: 'Fees' })
        : t({ comment: 'Deposit view title in account menu', message: 'Deposit' });

  return (
    <HoverMenu
      id={menuId}
      open={open}
      mounted={mounted}
      anchorEl={anchorEl}
      width={ACCOUNT_MENU_WIDTH}
      cardSx={menuCardSx}
      rootRef={setMenuRootRef}
      onMouseEnter={onEnter}
      onMouseLeave={handleMenuLeave}
      onEscape={handleEscape}
    >
      <Box
        key={swipe?.id ?? 'rest'}
        sx={{
          ...paneSx,
          ...(swipe ? { animation: `${swipeAnimation[swipe.dir]} ${SWIPE_MS}ms ${SWIPE_EASE}` } : null),
        }}
      >
        {view !== 'overview' ? (
          <>
            <MenuHeader title={detailTitle} onBack={handleBack} />
            {view === 'deposit' ? <DepositPanel isActive onClose={handleBack} autoFocus={false} /> : null}
            {view === 'withdraw' ? <WithdrawPanel isActive onClose={handleBack} autoFocus={false} /> : null}
            {view === 'fees' ? <FeesPanel /> : null}
          </>
        ) : (
          <AccountMenuOverview
            ensName={ensName}
            ensAvatar={ensAvatar}
            canWithdraw={canWithdraw}
            showReferral={showReferral}
            onDeposit={() => onViewChange('deposit')}
            onWithdraw={() => onViewChange('withdraw')}
            onFees={() => onViewChange('fees')}
            onDisconnect={handleDisconnect}
            onClose={onClose}
          />
        )}
      </Box>
    </HoverMenu>
  );
});
